import { useContext, useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import isEqual from "lodash.isequal";
import { Box, Button, CircularProgress } from "@mui/material";
import { AbilityContext } from "../../permission/Can";
import { Actions, Subjects } from "../../permission/constants";
import { shirtToShirtData } from "../../services/shirts/functions";
import LegitCheckService from "../../services/legitCheckService";
import ShirtsService from "../../services/shirts/shirtsService";

import BackdropLoaderService from "../BackdropLoader/backdropLoaderService";
import DialogService from "../Dialog/dialogService";
import LegitCheckResults from "../LegitCheckResults/LegitCheckResults";
import NotificationService from "../Notification/notificationService";
import EditShirtForm from "../ShirtForm/EditShirtForm";
import ShirtImages from "../ShirtImages/ShirtImages";
import { Shirt } from "../../entities/Shirt";
import ShirtDeleteConfirmModal from "./ShirtDeleteConfirmModal";

import {
  Image,
  NewShirt,
  Property,
  shirtDefault,
} from "../../services/shirts/types";
import {
  EXTRA_DATA as EXTRA_DATA_ENUM,
  LegitCheckItem,
} from "../../types/types";
import { Dictionary } from "../../types/types";

interface ShirtDetailOwnerProps {
  shirt: Shirt;
}

const ShirtDetailOwner = ({ shirt }: ShirtDetailOwnerProps) => {
  const [updatedShirt, setUpdatedShirt] = useState<Shirt>(shirt);
  const [shirtData, setShirtData] = useState<NewShirt>(shirtDefault);
  const [images, setImages] = useState<Array<Image>>([]);
  const [wornProperty, setWornProperty] = useState<Property | undefined>(
    undefined
  );
  const [lcResults, setLcResults] = useState<Array<LegitCheckItem> | undefined>(
    undefined
  );
  const [positiveChecks, setPositiveChecks] = useState<number | undefined>(
    undefined
  );
  const [errors, setErrors] = useState<Dictionary<boolean>>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const ability = useContext(AbilityContext);

  const { openMD, close: closeDialog, openXS } = DialogService();
  const { success, error } = NotificationService();
  const { updateImages, updateProperty, updateShirt } = ShirtsService();
  const { close: closeBDL } = BackdropLoaderService();
  const { getLegitChecks } = LegitCheckService();

  useEffect(() => {
    setUpdatedShirt({ ...shirt });
    if (shirt?.id && ability.can(Actions.VIEW, Subjects.LEGIT_CHECK)) {
      getLegitChecks(shirt.id).then((results) => {
        if (results) {
          const isLegitResults = results.filter((item) => item.is_legit).length;
          setLcResults(results);
          setPositiveChecks(isLegitResults);
        }
      });
    }
  }, [shirt]);

  useEffect(() => {
    // Only set initial data when the shirt prop changes, not when updatedShirt changes
    setShirtData(shirtToShirtData(shirt));
    setImages([...shirt.images]);

    if (shirt.properties && shirt.properties.length > 0) {
      const propertytWornEntry = shirt.properties.find(
        (obj: Property) =>
          obj.key === EXTRA_DATA_ENUM.MATCH_WORN ||
          obj.key === EXTRA_DATA_ENUM.MATCH_ISSUED
      );
      if (propertytWornEntry) {
        setWornProperty({ ...propertytWornEntry });
      }
    }
  }, [shirt]); // Changed from [updatedShirt] to [shirt]

  const legitCheckResults = () => {
    if (shirt?.id) {
      openMD(
        "Legit Check Results",
        <LegitCheckResults itemId={shirt.id} legitChecks={lcResults} />,
        []
      );
    }
  };

  const saveShirt = async () => {
    console.log("Save button clicked", { updatedShirt, shirtData, isLoading });
    setIsLoading(true);

    try {
      if (updatedShirt.id) {
        const oldData = shirtToShirtData(shirt);
        console.log("Comparing data:", { oldData, shirtData });
        let hasUpdates = false;

        // Update shirt data if changed
        if (!isEqual(oldData, shirtData)) {
          console.log("Shirt data has changed, updating...");
          const formattedShirt = {
            ...shirtData,
            purchase_date: shirtData.purchase_date
              ? dayjs(shirtData.purchase_date).format("YYYY/MM/DD")
              : undefined,
          };

          // Remove undefined player_id and number to make them optional
          if (formattedShirt.player_id === undefined) {
            delete formattedShirt.player_id;
          }
          if (formattedShirt.number === undefined) {
            delete formattedShirt.number;
          }

          console.log("Sending shirt update:", formattedShirt);
          const result = await updateShirt(updatedShirt.id, formattedShirt);
          if (result) {
            success("Shirt info updated successfully");
            hasUpdates = true;
          } else {
            error("Shirt info update gone wrong");
          }
        }

        // Update images if changed
        if (!isEqual(shirt.images, images)) {
          console.log("Images have changed, updating...");
          const result = await updateImages(updatedShirt.id, images);
          if (result) {
            success("Shirt images updated successfully");
            hasUpdates = true;
          } else {
            error("Shirt images update gone wrong");
          }
        }

        // Update properties if changed
        if (!isEqual(shirt.properties[0], wornProperty) && wornProperty?.id) {
          console.log("Properties have changed, updating...");
          const result = await updateProperty(
            updatedShirt.id,
            wornProperty?.id,
            wornProperty?.extra_data,
            shirt.properties[0].id
          );
          if (!result) {
            error("Properties update error");
          } else {
            success("Property created successfully");
            hasUpdates = true;
            closeDialog();
          }
        }

        if (!hasUpdates) {
          console.log("No changes detected");
          success("No changes to save");
        }
      } else {
        console.log("No shirt ID found");
        error("No shirt ID found");
      }
    } catch (err) {
      console.error("Save error:", err);
      error("An error occurred while saving");
    } finally {
      console.log("Setting loading to false");
      setIsLoading(false);
    }
  };

  const deleteCollection = () => {
    if (shirt.id) {
      openXS(
        "Delete Shirt",
        <ShirtDeleteConfirmModal shirtId={shirt.id} />,
        []
      );
      closeDialog();
    }
  };

  const updateField = (field: string, value: any | never) => {
    console.log("UpdateField called:", { field, value });

    // Update the shirt entity
    let copyShirt: Shirt = { ...updatedShirt };
    copyShirt[field as keyof typeof copyShirt] = value;
    setUpdatedShirt(copyShirt);

    // Also update the shirtData to keep them in sync
    let copyData: NewShirt = { ...shirtData };

    // Handle field name mappings between Shirt entity and NewShirt data
    switch (field) {
      case "is_private":
        copyData.private = value;
        break;
      case "purchase_price":
        copyData.purchase_price = Number(value);
        break;
      case "team_id":
        copyData.team_id = value;
        break;
      case "competition_id":
        copyData.competition_id = value;
        break;
      case "season_id":
        copyData.season_id = value;
        break;
      case "brand_id":
        copyData.brand_id = value;
        break;
      case "shirt_type_id":
        copyData.shirt_type_id = value;
        break;
      case "variant_id":
        copyData.variant_id = value;
        break;
      case "condition_id":
        copyData.condition_id = value;
        break;
      case "player_id":
        copyData.player_id = value;
        break;
      case "number":
        copyData.number = value;
        break;
      case "irresistible_offers":
        copyData.irresistible_offers = value;
        break;
      case "team_custom":
        copyData.team_custom = value;
        break;
      case "brand_custom":
        copyData.brand_custom = value;
        break;
      case "player_custom":
        copyData.player_custom = value;
        break;
      case "custom_number":
        copyData.custom_number = value;
        break;
      default:
        // For other fields, use direct mapping
        copyData[field as keyof typeof copyData] = value;
        break;
    }

    console.log("Updated shirtData:", copyData);
    setShirtData(copyData);
  };

  const removeImage = (index: number) => {
    let arr = [...images];
    if (index >= 0 && index < arr.length) {
      arr.splice(index, 1);
    }

    setImages(arr);
  };

  const addImage = (img: Image) => {
    setImages([...images, img]);
  };

  const validateDates = (
    date: Dayjs,
    errorKey: string,
    errorMessage: string
  ) => {
    if (date.isAfter(dayjs())) {
      error(errorMessage);
      setErrors((prevErrors) => ({
        ...prevErrors,
        [errorKey]: true,
      }));
    } else {
      setErrors((prevErrors) => {
        const { [errorKey]: _, ...rest } = prevErrors;
        return rest;
      });
    }
  };

  useEffect(() => {
    closeBDL();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images]);

  useEffect(() => {
    const purchaseDate = shirtData.purchase_date
      ? dayjs(shirtData.purchase_date)
      : undefined;
    const wornDate = wornProperty?.extra_data?.match?.date
      ? dayjs(wornProperty?.extra_data?.match?.date)
      : undefined;

    if (purchaseDate) {
      validateDates(
        purchaseDate,
        "purchase_date",
        "Purchase date must be earlier than today."
      );
    }

    if (wornDate) {
      validateDates(
        wornDate,
        "worn_date",
        "Match date must be earlier than today."
      );
    }

    if (wornDate && purchaseDate) {
      if (purchaseDate.isBefore(wornDate)) {
        error("Match date must be earlier than purchase date.");
        setErrors((prevErrors) => ({
          ...prevErrors,
          purchase_date: true,
          worn_date: true,
        }));
      } else {
        setErrors((prevErrors) => {
          const { purchase_date, worn_date, ...rest } = prevErrors;
          return rest;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wornProperty, shirtData.purchase_date]);

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 3 }}
      aria-label="ShritDetailOwner"
    >
      <Box>
        <ShirtImages
          removeImage={removeImage}
          addImage={addImage}
          images={images}
        />
      </Box>
      <Box>
        <EditShirtForm
          setShirtData={updateField}
          shirt={updatedShirt}
          setProperties={setWornProperty}
          errors={errors}
        />
      </Box>
      <Box sx={{ width: "fit-content", marginLeft: "auto" }}>
        <Button
          variant="text"
          sx={{
            textDecoration: "underline",
            textUnderlineOffset: "3px",
            fontFamily: "Manrope",
          }}
          onClick={legitCheckResults}
        >
          LC ({positiveChecks ? positiveChecks : "0"}/
          {lcResults ? lcResults.length : "0"})
        </Button>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box>
          <Button
            className="default-btn move-right"
            onClick={saveShirt}
            disabled={isLoading}
            sx={{
              width: "120px",
              height: "50px",
              textAlign: "center",
              padding: "0rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              color: "#fff",
              fontWeight: 600,
              fontStyle: "italic",
            }}
          >
            {isLoading && <CircularProgress size={16} color="inherit" />}
            <span>{isLoading ? "Saving..." : "Save"}</span>
          </Button>
        </Box>
        <Box>
          <Button
            className="default-btn move-right style-2"
            onClick={deleteCollection}
            disabled={isLoading}
            sx={{
              width: "120px",
              height: "50px",
              textAlign: "center",
              padding: "0rem",
              color: "#fff",
              fontWeight: 600,
              fontStyle: "italic",
            }}
          >
            <span>Delete</span>
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ShirtDetailOwner;
